export interface IndexedDBFile {
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  uploadStatus: 'pending' | 'uploading' | 'completed' | 'failed';
  createdAt: number;
}

export class IndexedDBService {
  private static instance: IndexedDBService;
  private db: IDBDatabase | null = null;
  private readonly dbName = 'DocUploadFileStore';
  private readonly dbVersion = 1;
  private readonly storeName = 'files';

  private constructor() {}

  public static getInstance(): IndexedDBService {
    if (!IndexedDBService.instance) {
      IndexedDBService.instance = new IndexedDBService();
    }
    return IndexedDBService.instance;
  }

  public static isSupported(): boolean {
    return 'indexedDB' in window;
  }

  private async initDB(): Promise<IDBDatabase> {
    if (!IndexedDBService.isSupported()) {
      throw new Error('IndexedDB is not supported in this browser');
    }

    if (this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName);

      request.onerror = () => {
        const errorMsg = request.error
          ? `Failed to open IndexedDB: ${request.error.name} - ${request.error.message}`
          : 'Failed to open IndexedDB: Unknown error';
        console.error(errorMsg);
        reject(new Error(errorMsg));
      };

      request.onblocked = () => {
        const errorMsg =
          'IndexedDB is blocked. Please close other tabs using this database.';
        console.warn(errorMsg);
        reject(new Error(errorMsg));
      };

      request.onsuccess = () => {
        this.db = request.result;

        // Handle database being closed unexpectedly
        this.db.onversionchange = () => {
          this.db?.close();
          this.db = null;
          console.warn('IndexedDB version changed, database connection closed');
        };

        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('name', 'name', { unique: false });
          store.createIndex('uploadStatus', 'uploadStatus', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      };
    });
  }

  public async storeFile(file: File, fileId: string): Promise<string> {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file object provided');
    }

    if (!fileId || typeof fileId !== 'string') {
      throw new Error('Invalid fileId provided');
    }

    const db = await this.initDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const fileRecord: IndexedDBFile = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      uploadStatus: 'pending',
      createdAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(fileRecord);

      // Add transaction error handler for rollback
      transaction.onerror = () => {
        reject(
          new Error(
            `Transaction failed: ${transaction.error?.message || 'Unknown error'}`
          )
        );
      };

      transaction.onabort = () => {
        reject(new Error('Transaction was aborted'));
      };

      request.onsuccess = () => {
        resolve(fileId);
      };

      request.onerror = () => {
        reject(
          new Error(
            `Failed to store file in IndexedDB: ${request.error?.message || 'Unknown error'}`
          )
        );
      };
    });
  }

  public async updateFileStatus(
    fileId: string,
    status: IndexedDBFile['uploadStatus']
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(fileId);

      getRequest.onsuccess = () => {
        const file = getRequest.result;
        if (file) {
          file.uploadStatus = status;
          const putRequest = store.put(file);

          putRequest.onsuccess = () => {
            resolve();
          };

          putRequest.onerror = () => {
            reject(new Error('Failed to update file status in IndexedDB'));
          };
        } else {
          reject(new Error('File not found in IndexedDB'));
        }
      };

      getRequest.onerror = () => {
        reject(new Error('Failed to get file from IndexedDB'));
      };
    });
  }

  public async deleteFile(fileId: string): Promise<boolean> {
    const db = await this.initDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.delete(fileId);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(new Error('Failed to delete file from IndexedDB'));
      };
    });
  }

  public async clearAll(): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to clear IndexedDB'));
      };
    });
  }
}
